import { useMemo } from "react";
import { FiX } from "react-icons/fi";
import { useFavStore } from "@/features/fav-filter/stores/favStore";
import {
  createFavoriteSettingsMediator,
  type FavoriteField,
  type FavoriteSettingsEffect,
  type FavoriteSettingsState,
} from "@/features/settings/mediators/favoriteSettingsMediator";
import {
  isSettingsEvent,
  type SettingsEvent,
} from "@/features/settings/mediators/settingsEvents";
import { SettingRow, SettingSection } from "@/features/settings/ui";
import { ComponentScope } from "@/shared/ui/component-hierarchy/ComponentScope";
import {
  useComponentNode,
  useHierarchyLifetime,
  useMediatorSnapshot,
} from "@/shared/ui/component-hierarchy/hooks";
import { Button, Input } from "@/shared/ui/form";

type InputChangeEvent = React.ChangeEvent<HTMLInputElement, HTMLInputElement>;

interface FavSettingsViewProps {
  readonly favWords: readonly string[];
  readonly favRegexes: readonly string[];
  readonly favWords2: readonly string[];
  readonly favRegexes2: readonly string[];
  readonly drafts: FavoriteSettingsState;
  readonly onEvent: (event: SettingsEvent) => void;
}

/**
 * お気に入り設定タブのコンテンツ（カタログ並び替え専用）
 */
export const FavSettings: React.FunctionComponent = () => {
  const {
    favWords,
    favRegexes,
    favWords2,
    favRegexes2,
    addFavWord,
    removeFavWord,
    addFavRegex,
    removeFavRegex,
    addFavWord2,
    removeFavWord2,
    addFavRegex2,
    removeFavRegex2,
  } = useFavStore();
  const mediator = useMemo(() => {
    const runEffect = (effect: FavoriteSettingsEffect): void => {
      if (effect.type === "alert") {
        alert(effect.message);
        return;
      }
      const actions: Record<
        FavoriteField,
        { add: (value: string) => void; remove: (value: string) => void }
      > = {
        word: { add: addFavWord, remove: removeFavWord },
        regex: { add: addFavRegex, remove: removeFavRegex },
        word2: { add: addFavWord2, remove: removeFavWord2 },
        regex2: { add: addFavRegex2, remove: removeFavRegex2 },
      };
      actions[effect.field][effect.type](effect.value);
    };
    return createFavoriteSettingsMediator(runEffect);
  }, [
    addFavRegex,
    addFavRegex2,
    addFavWord,
    addFavWord2,
    removeFavRegex,
    removeFavRegex2,
    removeFavWord,
    removeFavWord2,
  ]);
  useHierarchyLifetime(mediator);
  const node = useComponentNode((event) => {
    if (
      !isSettingsEvent(event) ||
      (event.type !== "settings/favorite-draft-changed" &&
        event.type !== "settings/favorite-add-requested" &&
        event.type !== "settings/favorite-remove-requested")
    ) {
      return "unhandled";
    }
    return mediator.dispatch(event).status === "handled"
      ? "handled"
      : "unhandled";
  });
  const drafts = useMediatorSnapshot(mediator);

  return (
    <ComponentScope node={node}>
      <FavSettingsView
        favWords={favWords}
        favRegexes={favRegexes}
        favWords2={favWords2}
        favRegexes2={favRegexes2}
        drafts={drafts}
        onEvent={(event): void => {
          node.dispatch(event);
        }}
      />
    </ComponentScope>
  );
};

export const FavSettingsView: React.FunctionComponent<FavSettingsViewProps> = ({
  favWords,
  favRegexes,
  favWords2,
  favRegexes2,
  drafts,
  onEvent,
}: FavSettingsViewProps) => {
  const requestAdd = (field: FavoriteField): void => {
    onEvent({ type: "settings/favorite-add-requested", field });
  };

  return (
    <>
      <SettingSection
        title="超お気に入りワード"
        description="本文部分一致でカタログ最上位に枠線付きで表示"
      >
        <SettingRow>
          <Input
            type="text"
            placeholder="超お気に入りワードを入力"
            className="w-full"
            value={drafts.word2}
            onChange={(e: InputChangeEvent): void =>
              onEvent({
                type: "settings/favorite-draft-changed",
                field: "word2",
                value: e.target.value,
              })
            }
            onKeyDown={(e: React.KeyboardEvent): void => {
              if (e.key === "Enter") {
                requestAdd("word2");
              }
            }}
          />
          <Button variant="primary" onClick={(): void => requestAdd("word2")}>
            追加
          </Button>
        </SettingRow>
        {favWords2.length > 0 ? (
          <ul
            aria-label="超お気に入りワード"
            className="m-0 list-none space-y-2 p-0"
          >
            {favWords2.map((word) => (
              <li key={word}>
                <SettingRow>
                  <p className="min-w-0 flex-1 truncate text-foreground">
                    {word}
                  </p>
                  <Button
                    variant="ghost"
                    onClick={(): void =>
                      onEvent({
                        type: "settings/favorite-remove-requested",
                        field: "word2",
                        value: word,
                      })
                    }
                    icon={<FiX size={16} />}
                  >
                    削除
                  </Button>
                </SettingRow>
              </li>
            ))}
          </ul>
        ) : (
          <SettingRow>
            <p className="text-muted-foreground">なし</p>
          </SettingRow>
        )}
      </SettingSection>

      <SettingSection
        title="超お気に入り正規表現"
        description="正規表現パターンでカタログ最上位に表示"
      >
        <SettingRow>
          <Input
            type="text"
            placeholder="正規表現パターン（例: ^fav.*）"
            className="w-full"
            value={drafts.regex2}
            onChange={(e: InputChangeEvent): void =>
              onEvent({
                type: "settings/favorite-draft-changed",
                field: "regex2",
                value: e.target.value,
              })
            }
            onKeyDown={(e: React.KeyboardEvent): void => {
              if (e.key === "Enter") {
                requestAdd("regex2");
              }
            }}
          />
          <Button variant="primary" onClick={(): void => requestAdd("regex2")}>
            追加
          </Button>
        </SettingRow>
        {favRegexes2.length > 0 ? (
          <ul
            aria-label="超お気に入り正規表現"
            className="m-0 list-none space-y-2 p-0"
          >
            {favRegexes2.map((regex) => (
              <li key={regex}>
                <SettingRow>
                  <code className="min-w-0 flex-1 truncate text-sm text-foreground">
                    {regex}
                  </code>
                  <Button
                    variant="ghost"
                    onClick={(): void =>
                      onEvent({
                        type: "settings/favorite-remove-requested",
                        field: "regex2",
                        value: regex,
                      })
                    }
                    icon={<FiX size={16} />}
                  >
                    削除
                  </Button>
                </SettingRow>
              </li>
            ))}
          </ul>
        ) : (
          <SettingRow>
            <p className="text-muted-foreground">なし</p>
          </SettingRow>
        )}
      </SettingSection>

      <SettingSection
        title="お気に入りワード"
        description="本文部分一致でカタログ上位に表示"
      >
        <SettingRow>
          <Input
            type="text"
            placeholder="お気に入りワードを入力"
            className="w-full"
            value={drafts.word}
            onChange={(e: InputChangeEvent): void =>
              onEvent({
                type: "settings/favorite-draft-changed",
                field: "word",
                value: e.target.value,
              })
            }
            onKeyDown={(e: React.KeyboardEvent): void => {
              if (e.key === "Enter") {
                requestAdd("word");
              }
            }}
          />
          <Button variant="primary" onClick={(): void => requestAdd("word")}>
            追加
          </Button>
        </SettingRow>
        {favWords.length > 0 ? (
          <ul
            aria-label="お気に入りワード"
            className="m-0 list-none space-y-2 p-0"
          >
            {favWords.map((word) => (
              <li key={word}>
                <SettingRow>
                  <p className="min-w-0 flex-1 truncate text-foreground">
                    {word}
                  </p>
                  <Button
                    variant="ghost"
                    onClick={(): void =>
                      onEvent({
                        type: "settings/favorite-remove-requested",
                        field: "word",
                        value: word,
                      })
                    }
                    icon={<FiX size={16} />}
                  >
                    削除
                  </Button>
                </SettingRow>
              </li>
            ))}
          </ul>
        ) : (
          <SettingRow>
            <p className="text-muted-foreground">なし</p>
          </SettingRow>
        )}
      </SettingSection>

      <SettingSection
        title="お気に入り正規表現"
        description="正規表現パターンでカタログ上位に表示"
      >
        <SettingRow>
          <Input
            type="text"
            placeholder="正規表現パターン（例: ^fav.*）"
            className="w-full"
            value={drafts.regex}
            onChange={(e: InputChangeEvent): void =>
              onEvent({
                type: "settings/favorite-draft-changed",
                field: "regex",
                value: e.target.value,
              })
            }
            onKeyDown={(e: React.KeyboardEvent): void => {
              if (e.key === "Enter") {
                requestAdd("regex");
              }
            }}
          />
          <Button variant="primary" onClick={(): void => requestAdd("regex")}>
            追加
          </Button>
        </SettingRow>
        {favRegexes.length > 0 ? (
          <ul
            aria-label="お気に入り正規表現"
            className="m-0 list-none space-y-2 p-0"
          >
            {favRegexes.map((regex) => (
              <li key={regex}>
                <SettingRow>
                  <code className="min-w-0 flex-1 truncate text-sm text-foreground">
                    {regex}
                  </code>
                  <Button
                    variant="ghost"
                    onClick={(): void =>
                      onEvent({
                        type: "settings/favorite-remove-requested",
                        field: "regex",
                        value: regex,
                      })
                    }
                    icon={<FiX size={16} />}
                  >
                    削除
                  </Button>
                </SettingRow>
              </li>
            ))}
          </ul>
        ) : (
          <SettingRow>
            <p className="text-muted-foreground">なし</p>
          </SettingRow>
        )}
      </SettingSection>

      <SettingSection title="正規表現について">
        <SettingRow>
          <aside
            aria-label="正規表現の例"
            className="text-sm text-muted-foreground space-y-3 w-full"
          >
            <p>正規表現は高度な選択を行う特殊記法です。</p>
            <figure className="space-y-1">
              <figcaption>例：AとBを両方含む</figcaption>
              <code className="block text-foreground bg-muted px-2 py-1 rounded text-xs">
                ^(?=.*A)(?=.*B).*
              </code>
            </figure>
            <figure className="space-y-1">
              <figcaption>例：Aは含むが、Bは含まない</figcaption>
              <code className="block text-foreground bg-muted px-2 py-1 rounded text-xs">
                ^(?=.*A)(?!.*B).*
              </code>
            </figure>
            <figure className="space-y-1">
              <figcaption>例：Aを含まない</figcaption>
              <code className="block text-foreground bg-muted px-2 py-1 rounded text-xs">
                ^(?!.*A).*
              </code>
            </figure>
          </aside>
        </SettingRow>
      </SettingSection>
    </>
  );
};
